import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import './Tutorial.scss';
// Padding (px) of the spotlight cut-out around the target element.
const SPOTLIGHT_PAD = 8;
const CARD_WIDTH = 360;
const GAP = 16;
// The generic tour engine. It reads TUTORIAL_STEPS and, per step, either shows a
// centered "intro" card or spotlights a target element (dimming everything else)
// with a tooltip card beside it. All copy lives in tutorialSteps.ts.
export function Tutorial({ open, onClose, tour, onComplete }) {
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const steps = tour.steps;
    const step = steps[index];
    // Keep the overlay mounted briefly after `open` flips to false so it can play
    // an exit animation instead of vanishing abruptly — matters most on the
    // manager → editor handoff, where one tour fades out and the next fades in.
    const [rendered, setRendered] = useState(open);
    useEffect(() => {
        if (open) {
            setRendered(true);
            return;
        }
        const t = window.setTimeout(() => setRendered(false), 240);
        return () => window.clearTimeout(t);
    }, [open]);
    // Restart from the first step whenever the tour is (re)opened or swapped.
    useEffect(() => { if (open)
        setIndex(0); }, [open, tour.id]);
    // Measure the current step's target (scrolling it into view first). Intro
    // steps — or spotlight steps whose target isn't on screen — measure to null,
    // which renders a centered card.
    const measure = useCallback(() => {
        if (!open || !step)
            return;
        if (step.kind === 'spotlight' && step.target) {
            const el = document.querySelector(step.target);
            if (el) {
                // Only scroll when the target isn't already comfortably on screen.
                // An unconditional scrollIntoView({block:'center'}) yanks the whole
                // viewport on every step — jarring in the editor, where most
                // targets (toolbar, tools, the big map) are already visible.
                const r = el.getBoundingClientRect();
                const vw = window.innerWidth, vh = window.innerHeight;
                const visW = Math.min(r.right, vw) - Math.max(r.left, 0);
                const visH = Math.min(r.bottom, vh) - Math.max(r.top, 0);
                const enough = visW > 0 && visH > 0 &&
                    visW >= Math.min(r.width, vw * 0.5) &&
                    visH >= Math.min(r.height, vh * 0.5);
                if (enough) {
                    setRect(r);
                    return;
                }
                el.scrollIntoView({ block: 'center', inline: 'center' });
                setRect(el.getBoundingClientRect());
                return;
            }
            // Adaptive: the target isn't on screen (e.g. an empty account with no
            // level card). Skip this step rather than show a dangling centered
            // card — unless it's the last step.
            if (index < steps.length - 1) {
                setIndex(i => i + 1);
                return;
            }
        }
        setRect(null);
    }, [open, step, index, steps.length]);
    useLayoutEffect(() => { measure(); }, [measure]);
    // Keep the spotlight glued to the target through resizes/scrolls.
    useEffect(() => {
        if (!open)
            return;
        const onChange = () => measure();
        window.addEventListener('resize', onChange);
        window.addEventListener('scroll', onChange, true);
        return () => {
            window.removeEventListener('resize', onChange);
            window.removeEventListener('scroll', onChange, true);
        };
    }, [open, measure]);
    const finish = useCallback(() => {
        try {
            localStorage.setItem(tour.seenKey, '1');
        }
        catch ( /* ignore */_a) { /* ignore */ }
        onClose();
    }, [onClose, tour.seenKey]);
    const isFirst = index === 0;
    const isLast = index === steps.length - 1;
    const next = useCallback(() => {
        if (isLast) {
            finish();
            onComplete === null || onComplete === void 0 ? void 0 : onComplete();
        }
        else
            setIndex(i => i + 1);
    }, [isLast, finish, onComplete]);
    const back = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);
    // Keyboard: Esc closes, ←/→ navigate.
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                finish();
            else if (e.key === 'ArrowRight' || e.key === 'Enter')
                next();
            else if (e.key === 'ArrowLeft')
                back();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, finish, next, back]);
    if (!rendered || !step)
        return null;
    // While `open` is false but still rendered, we're playing the exit animation.
    const closing = !open;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Position the card: centered when there's no target; otherwise beside the
    // spotlight. Below/above when there's room, else to the side (good for tall
    // targets like the map grid). Always clamped fully on screen.
    const EST_CARD_H = 280; // rough card height, used for fit checks + clamping
    let cardStyle;
    if (!rect) {
        cardStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
    }
    else {
        const spaceBelow = vh - rect.bottom;
        const spaceAbove = rect.top;
        const spaceRight = vw - rect.right;
        const spaceLeft = rect.left;
        const horizFits = (s) => s >= CARD_WIDTH + GAP;
        const tall = rect.height > vh * 0.55;
        let place;
        if (step.placement === 'top')
            place = 'above';
        else if (step.placement === 'bottom')
            place = 'below';
        else if (!tall && spaceBelow >= EST_CARD_H)
            place = 'below';
        else if (!tall && spaceAbove >= EST_CARD_H)
            place = 'above';
        else if (horizFits(spaceRight))
            place = 'right';
        else if (horizFits(spaceLeft))
            place = 'left';
        else
            place = spaceBelow >= spaceAbove ? 'below' : 'above';
        const clampLeft = (l) => Math.round(Math.min(Math.max(GAP, l), vw - CARD_WIDTH - GAP));
        const clampTop = (t) => Math.round(Math.min(Math.max(GAP, t), vh - EST_CARD_H - GAP));
        const centerLeft = rect.left + rect.width / 2 - CARD_WIDTH / 2;
        const centerTop = rect.top + rect.height / 2 - EST_CARD_H / 2;
        cardStyle =
            place === 'below' ? { left: clampLeft(centerLeft), top: clampTop(rect.bottom + GAP) }
                : place === 'above' ? { left: clampLeft(centerLeft), top: clampTop(rect.top - EST_CARD_H - GAP) }
                    : place === 'right' ? { left: clampLeft(rect.right + GAP), top: clampTop(centerTop) }
                        : { left: clampLeft(rect.left - CARD_WIDTH - GAP), top: clampTop(centerTop) };
    }
    const StepIcon = step.icon;
    const hasImage = step.kind === 'intro' && !!step.image;
    // Progress dots: filled dots (0..index) run a cyan→mint gradient across the
    // whole strip, matching the Figma carousel; unfilled dots stay faint white.
    const dotColor = (i) => {
        if (i > index)
            return 'rgba(255, 255, 255, 0.2)';
        const t = steps.length > 1 ? i / (steps.length - 1) : 0;
        const ch = (a, b) => Math.round(a + (b - a) * t);
        return `rgb(${ch(0x00, 0x02)}, ${ch(0xbf, 0xff)}, ${ch(0xff, 0xb3)})`;
    };
    const spotlightStyle = rect ? {
        left: Math.round(rect.left - SPOTLIGHT_PAD),
        top: Math.round(rect.top - SPOTLIGHT_PAD),
        width: Math.round(rect.width + SPOTLIGHT_PAD * 2),
        height: Math.round(rect.height + SPOTLIGHT_PAD * 2),
    } : null;
    return createPortal(_jsxs("div", { className: `tour-root${closing ? ' tour-root--closing' : ''}`, role: "dialog", "aria-modal": "true", "aria-label": "Product tour", children: [!rect && _jsx("div", { className: "tour-scrim" }), spotlightStyle && _jsx("div", { className: "tour-spotlight", style: spotlightStyle }), _jsxs("div", { className: `tour-card ${rect ? 'tour-card--anchored' : 'tour-card--center'} ${hasImage ? 'tour-card--intro' : ''}`, style: cardStyle, children: [hasImage && (_jsx("div", { className: "tour-image", children: _jsx("img", { src: step.image, alt: "", "aria-hidden": true }) })), _jsx("button", { className: "tour-close", onClick: finish, "aria-label": "Skip tutorial", children: _jsx(FaTimes, {}) }), _jsxs("div", { className: "tour-content", children: [step.kind === 'intro' && !hasImage && StepIcon && (_jsx("div", { className: "tour-icon", "aria-hidden": true, children: _jsx(StepIcon, {}) })), _jsxs("div", { className: "tour-heading", children: [_jsx("h3", { className: "tour-title", children: step.title }), _jsx("p", { className: "tour-body", children: step.body })] }), _jsxs("div", { className: "tour-footer", children: [_jsx("div", { className: "tour-progress", "aria-label": `Step ${index + 1} of ${steps.length}`, children: steps.map((s, i) => (_jsx("span", { className: "tour-dot", style: { background: dotColor(i) } }, s.id))) }), _jsxs("div", { className: "tour-actions", children: [!isFirst && (_jsxs("button", { className: "tour-btn ghost", onClick: back, children: [_jsx(FaArrowLeft, {}), " Back"] })), _jsxs("button", { className: "tour-btn primary", onClick: next, children: ["Next ", _jsx(FaArrowRight, {})] })] })] }), !isLast && _jsx("button", { className: "tour-skip", onClick: finish, children: "Skip tour" })] })] })] }), document.body);
}
export default Tutorial;
